/**
 * Shared helpers for the admin ops components (ops dashboard, fraud, funnel,
 * reconciliation, etc.). Keeps the individual files focused on rendering.
 */

// ---- localStorage ----

export const readStorage = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

export const writeStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // quota or disabled storage
  }
};

// ---- admin identity ----

export const currentAdmin = () => {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return { id: "me", name: "me" };
    const user = JSON.parse(raw);
    return {
      id: user.id || user.email || "me",
      name: user.name || user.email || user.id || "me",
    };
  } catch {
    return { id: "me", name: "me" };
  }
};

// ---- fetch with graceful fallback ----

export const authedFetch = async (url, options = {}) => {
  const token = localStorage.getItem("token");
  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(url, { ...options, headers });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

// ---- formatting ----

export const formatCurrency = (amount, currency = "USD") => {
  const n = Number(amount) || 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
  }).format(n);
};

export const formatPercent = (value, digits = 1) => {
  const n = Number(value) || 0;
  return `${n.toFixed(digits)}%`;
};

export const formatRelative = (timestamp) => {
  if (!timestamp) return "—";
  const t = new Date(timestamp).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = Date.now() - t;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

// ---- audit log ----
//
// All policy / taxonomy / feature-flag / freeze actions write to a shared
// client-side audit log so every console can render a "recent activity" feed.
// In production this should be backed by the server; the shape is stable so
// swapping the store is a one-line change.

const AUDIT_KEY = "opsdash.auditLog.v1";
const MAX_AUDIT_ENTRIES = 500;

export const appendAudit = (entry) => {
  const log = readStorage(AUDIT_KEY, []);
  const admin = currentAdmin();
  log.unshift({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    at: new Date().toISOString(),
    actorId: admin.id,
    actorName: admin.name,
    ...entry,
  });
  writeStorage(AUDIT_KEY, log.slice(0, MAX_AUDIT_ENTRIES));
};

export const readAudit = (filter = {}) => {
  const log = readStorage(AUDIT_KEY, []);
  return log.filter((e) => {
    if (filter.area && e.area !== filter.area) return false;
    if (filter.subjectId && e.subjectId !== filter.subjectId) return false;
    return true;
  });
};

export const clearAudit = () => writeStorage(AUDIT_KEY, []);

// ---- themed classes ----

export const themedCard = (darkMode) =>
  darkMode ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200";

export const themedMuted = (darkMode) =>
  darkMode ? "text-gray-400" : "text-gray-500";

export const themedInput = (darkMode) =>
  darkMode
    ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500"
    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400";
